import classNames from "classnames";
import { ConditionalLink } from "components/conditionalLink";
import ConditionalToolTip from "components/design/conditionalToolTip";
import Async from "components/DesignSystem/AsyncComponents/Async";
import { Button } from "components/DesignSystem/Button/Button";
import {
  Combobox,
  OptionData,
} from "components/DesignSystem/Combobox/Combobox";
import Modal from "components/DesignSystem/Modal/Modal";
import { StatusModal } from "components/DesignSystem/StatusModal/StatusModal";
import { Switch } from "components/DesignSystem/Switch/Switch";
import { DisableAccountsReceivableModal } from "components/DisableAccountsReceivableModal/DisableAccountsReceivableModal";
import { EditFinancialsEmailModal } from "components/EditFinancialsEmailModal/EditFinancialsEmailModal";
import { EditLockDateModal } from "components/EditLockDateModal/EditLockDateModal";
import { EnableAccountsReceivableModal } from "components/EnableAccountsReceivableModal/EnableAccountsReceivableModal";
import { EditPencil } from "components/icons/EditPencil";
import { Info } from "components/icons/Info";
import { Settings } from "components/icons/Navbar/Settings";
import { PlusIcon } from "components/icons/PlusIcon";
import { TrashSimple } from "components/icons/TrashSimple";
import { BooksSoftwareSubscriptionsModal } from "components/BooksSoftwareSubscriptionsModal";
import { FinallyImportSettingRow } from "components/FinallyImport/FinallyImportSettingRow";
import { ImportQBO } from "components/QBO/ImportQBO";
import { QBOImportReview } from "components/QBO/QBOImportReview";
import { RemoveLockDateModal } from "components/RemoveLockDateModal/RemoveLockDateModal";
import {
  ACCOUNTING_METHOD_TYPES,
  STRIPE_FREQUENCY_TYPES,
  TAX_YEAR_END_TYPES,
} from "constants/bookkeeping";
import {
  ACCOUNTING_METHOD_OPTIONS,
  STRIPE_FREQUENCY_OPTIONS,
  TAX_YEAR_OPTIONS,
} from "constants/booksSettings";
import { ACCOUNT_CONNECTION_TYPE } from "constants/dataSource";
import dayjs from "dayjs";
import { FormikHelpers } from "formik";
import { useAuth } from "hooks/useAuth";
import { useCurrentEntity } from "hooks/useCurrentEntity";
import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useDataSourceSegregate } from "hooks/useDataSourceSegregate";
import { useModal } from "hooks/useModal";
import { usePageTitle } from "hooks/usePageTitle";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { MultiValue, SingleValue } from "react-select";
import { useGetEntityBanksQuery } from "store/apis/bankConnections";
import {
  useGetBookkeepingLogsQuery,
  useGetBookkeepingSettingsQuery,
  useUpdateBookkeepingSettingsMutation,
} from "store/apis/booksLockDate";
import { useGetFileUploadQBOfileQuery } from "store/apis/qbo";
import { BOOKS_IMPORT_STATUS } from "types/Models/books";
import { BackendError } from "types/utils/error";
import { capitalizeFirstLetter } from "utils/capitalizeFirstLetter";
import { formatDate } from "utils/formatDate";
import { SectionHeader, SettingRow } from "./BooksSettings";
import { SettingsAccordion } from "./SettingsAccordion";
import AutomationSettings from "./AutomationSettings";
import { useHasWritePermission } from "hooks/useHasWritePermission";
import { WRITE_PERMISSION_KEYS } from "constants/permissions";
import { LockedButton } from "components/DesignSystem/Button/LockedButton";
import { LockedUi } from "components/LockedUi/LockedUi";

type LockDateFormValues = {
  accounting_lock_date: string;
};

export const AccountingSettings = ({ entityId }: { entityId: string }) => {
  usePageTitle("Accounting Settings");
  const editModal = useModal();
  const removeModal = useModal();
  const enableAccountsReceivableModal = useModal();
  const disableAccountsReceivableModal = useModal();
  const taxYearConfirmModal = useModal();
  const importQBO = useModal();
  const qboReviewModal = useModal();
  const subscriptionModal = useModal();
  const editEmailModal = useModal();

  const [pendingTaxYear, setPendingTaxYear] = useState<{
    value: string;
    label: string;
  } | null>(null);

  const [accountsType, setAccountsType] = useState<"receivable" | "payable">(
    "receivable"
  );

  const [shouldPollQBO, setShouldPollQBO] = useState(true);

  const { successToast, alertToast } = useToast();
  const { uuid } = useAuth();
  const { isAdmin, isPracticePro } = useRoleBasedView();
  const { search } = useLocation();

  const entityDetail = useCurrentEntity();
  const group = useCurrentGroupContext();

  const {
    data: lockDateData,
    isLoading,
    isSuccess,
  } = useGetBookkeepingSettingsQuery({ entityId }, { skip: !entityId });

  const { data: plaidConnections } = useGetEntityBanksQuery(
    {
      groupId: group.uuid!,
      entityId: entityId!,
    },
    { skip: !group.uuid || !entityId }
  );

  const { stripeConnectedBank } = useDataSourceSegregate(plaidConnections!);

  const [updateBooksSetting, { isLoading: isUpdating }] =
    useUpdateBookkeepingSettingsMutation();

  const { data: logsData } = useGetBookkeepingLogsQuery(
    { entityId },
    { skip: !entityId }
  );

  const stripeFrequencyLog = logsData?.logs?.find(
    (log) => log.field_name === "stripe_aggregate_frequency"
  );

  const { data: qboFileUploadData } = useGetFileUploadQBOfileQuery(
    { entityId },
    {
      skip: !entityId || !shouldPollQBO,
      pollingInterval: 10000,
    }
  );

  const qboImportStatus = qboFileUploadData?.status;

  useEffect(() => {
    if (
      qboImportStatus === BOOKS_IMPORT_STATUS.COMPLETED ||
      qboImportStatus === BOOKS_IMPORT_STATUS.NOT_STARTED
    ) {
      setShouldPollQBO(false);
    }
  }, [qboImportStatus]);

  const canUpdate = isAdmin || logsData?.logs?.[0]?.changed_by.uuid === uuid;
  const hasWritePermission = useHasWritePermission(
    WRITE_PERMISSION_KEYS.ACCOUNTING
  );

  const handleAddLockDateClick = () => {
    editModal.open();
  };

  const handleEditClick = () => {
    editModal.open();
  };

  const handleRemoveLock = () => {
    removeModal.open();
  };

  const getQBOImportButtonConfig = () => {
    switch (qboImportStatus) {
      case BOOKS_IMPORT_STATUS.IN_PROGRESS:
        return {
          text: "In progress",
          disabled: true,
          onClick: () => {},
        };
      case BOOKS_IMPORT_STATUS.COMPLETED:
        return {
          text: "View",
          disabled: false,
          onClick: () => qboReviewModal.open(),
        };
      case BOOKS_IMPORT_STATUS.NOT_STARTED:
        return {
          text: "Import",
          disabled: false,
          onClick: () => importQBO.open(),
        };
      default:
        return {
          text: "Import",
          disabled: true,
          onClick: () => {},
        };
    }
  };

  const qboButtonConfig = getQBOImportButtonConfig();

  const formValues = {
    accounting_lock_date: lockDateData?.accounting_lock_date || "",
  };

  const confirmDeleteLock = async () => {
    if (!entityId) return;
    try {
      await updateBooksSetting({
        entityId,
        accounting_lock_date: null,
      }).unwrap();

      removeModal.close();
      successToast({
        title: "Lock date removed",
        message: "Your financials are now unlocked.",
      });
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ||
            "Failed to remove lock date",
        },
        error as Error
      );
    }
  };

  const handleConfirmSubmit = async (
    values: LockDateFormValues,
    { setSubmitting }: FormikHelpers<LockDateFormValues>
  ) => {
    if (!entityId) {
      setSubmitting(false);
      return;
    }

    try {
      await updateBooksSetting({
        entityId,
        accounting_lock_date: values.accounting_lock_date,
      }).unwrap();

      editModal.close();
      successToast({
        title: "Lock date updated",
        message: `Your financials are now locked until ${dayjs(
          values.accounting_lock_date
        ).format("d MMM, YYYY")}`,
      });
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ||
            "Failed to update lock date",
        },
        error as Error
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleAccountsReceivableToggle = async (enabled: boolean) => {
    if (!entityId) return;

    setAccountsType("receivable");
    if (enabled) {
      enableAccountsReceivableModal.open();
    } else {
      disableAccountsReceivableModal.open();
    }
  };

  const handleAccountsPayableToggle = async (enabled: boolean) => {
    if (!entityId) return;

    setAccountsType("payable");
    if (enabled) {
      enableAccountsReceivableModal.open();
    } else {
      disableAccountsReceivableModal.open();
    }
  };

  const onSendFinancialsToggle = async (checked: boolean) => {
    if (!entityId) return;

    try {
      await updateBooksSetting({
        entityId,
        send_completed_financials_email: checked,
      }).unwrap();
      successToast({ message: "Setting updated successfully" });
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ||
            "Failed to update setting",
        },
        error as Error
      );
    }
  };

  const onAccountingMethodChange = async (
    value: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    if (value instanceof Array || !value) {
      return;
    }

    if (!entityId || value.value === lockDateData?.accounting_method) return;

    try {
      await updateBooksSetting({
        entityId,
        accounting_method: value.value as ACCOUNTING_METHOD_TYPES,
      }).unwrap();

      successToast({
        title: "Accounting method updated",
        message: `Your accounting method is now set to ${value.label}.`,
      });
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ||
            "Failed to update accounting method",
        },
        error as Error
      );
    }
  };

  const onTaxYearChange = (
    value: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    if (value instanceof Array) {
      return;
    }

    if (!entityId || !value || value.value === lockDateData?.tax_year_end)
      return;

    const taxYearValue = value?.value ?? "";
    const taxYearLabel = value?.label as string;

    setPendingTaxYear({ value: taxYearValue, label: taxYearLabel });
    taxYearConfirmModal.open();
  };

  const handleConfirmTaxYearChange = async () => {
    if (!entityId || !pendingTaxYear) return;

    try {
      await updateBooksSetting({
        entityId,
        tax_year_end: pendingTaxYear.value as TAX_YEAR_END_TYPES,
      }).unwrap();

      successToast({
        title: "Tax year end updated",
        message: `Your tax year end is now set to ${capitalizeFirstLetter(
          pendingTaxYear.label.toLocaleLowerCase()
        )}.`,
      });
      taxYearConfirmModal.close();
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ||
            "Failed to update tax year",
        },
        error as Error
      );
    }
  };

  const onStripeFrequencyChange = async (
    value: MultiValue<OptionData> | SingleValue<OptionData>
  ) => {
    if (value instanceof Array || !value) {
      return;
    }

    if (!entityId || value.value === lockDateData?.stripe_aggregate_frequency)
      return;

    try {
      await updateBooksSetting({
        entityId,
        stripe_aggregate_frequency: value.value as STRIPE_FREQUENCY_TYPES,
      }).unwrap();

      successToast({
        title: "Stripe frequency updated",
        message: `Your Stripe summary frequency has been updated to ${value.label}.`,
      });
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ||
            "Failed to update Stripe frequency",
        },
        error as Error
      );
    }
  };

  const confirmEnableAccountsReceivable = async ({
    date,
  }: {
    date: string;
  }) => {
    if (!entityId) return;
    try {
      await updateBooksSetting({
        entityId,
        accounts_receivable_enabled: true,
        accounts_receivable_from_date: date,
      }).unwrap();

      enableAccountsReceivableModal.close();
      successToast({
        title: "Auto-Manage Accounts Receivable enabled",
        message:
          "This will enable automatic handling of Accounts Receivable for new invoices.",
      });
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ||
            "Failed to enable Auto-Manage Accounts Receivable",
        },
        error as Error
      );
    }
  };

  const confirmDisableAccountsReceivable = async () => {
    if (!entityId) return;
    try {
      await updateBooksSetting({
        entityId,
        accounts_receivable_enabled: false,
        defer_revenue: false,
      }).unwrap();

      disableAccountsReceivableModal.close();
      successToast({
        title: "Auto-Manage Accounts Receivable disabled",
        message:
          "This will disable automatic handling of Accounts Receivable for new invoices.",
      });
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ||
            "Failed to disable Auto-Manage Accounts Receivable",
        },
        error as Error
      );
    }
  };

  const confirmEnableAccountsPayable = async ({ date }: { date: string }) => {
    if (!entityId) return;
    try {
      await updateBooksSetting({
        entityId,
        accounts_payable_enabled: true,
        accounts_payable_from_date: date,
      }).unwrap();

      enableAccountsReceivableModal.close();
      successToast({
        title: "Auto-Manage Accounts Payable enabled",
        message:
          "This will enable automatic handling of Accounts Payable for new bills.",
      });
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ||
            "Failed to enable Auto-Manage Accounts Payable",
        },
        error as Error
      );
    }
  };

  const confirmDisableAccountsPayable = async () => {
    if (!entityId) return;
    try {
      await updateBooksSetting({
        entityId,
        accounts_payable_enabled: false,
        defer_expenses: false,
      }).unwrap();

      disableAccountsReceivableModal.close();
      successToast({
        title: "Auto-Manage Accounts Payable disabled",
        message:
          "This will disable automatic handling of Accounts Payable for new bills.",
      });
    } catch (error) {
      alertToast(
        {
          message:
            (error as BackendError).data?.error?.message ||
            "Failed to disable Auto-Manage Accounts Payable",
        },
        error as Error
      );
    }
  };
  const location = useLocation();
  const { hash } = location;
  const isAutomation = hash === "#AUTOMATION";

  return (
    <Async.Root isLoading={isLoading} isSuccess={isSuccess} isEmpty={false}>
      <Async.Success>
        <div className="t-pb-24">
          <SettingsAccordion.Root
            defaultValue={isAutomation ? ["AUTOMATION"] : ["GENERAL"]}
          >
            {/* General Section */}
            <SettingsAccordion.Item value="GENERAL">
              <SettingsAccordion.Trigger>
                <SectionHeader
                  icon={<Settings className="t-size-3.5" />}
                  title="General"
                />
              </SettingsAccordion.Trigger>

              <SettingsAccordion.Content>
                <SettingRow
                  title="Accounting Method"
                  description="We'll use this to determine how your income and expenses are shown everywhere."
                >
                  <div className="t-w-56 sm:t-w-full">
                    <Combobox
                      options={ACCOUNTING_METHOD_OPTIONS}
                      value={ACCOUNTING_METHOD_OPTIONS.find(
                        (method) =>
                          method.value === lockDateData?.accounting_method
                      )}
                      onChange={onAccountingMethodChange}
                      isClearable={false}
                      isDisabled={!hasWritePermission}
                    />
                  </div>
                </SettingRow>

                <SettingRow
                  title="Tax Year End"
                  description="We'll use this to populated your filters of current year, previous year etc, and will handle the retained earnings, tax deadlines."
                >
                  <div className="t-w-56 sm:t-w-full">
                    <Combobox
                      options={TAX_YEAR_OPTIONS}
                      value={TAX_YEAR_OPTIONS.find(
                        (year) => year.value === lockDateData?.tax_year_end
                      )}
                      onChange={onTaxYearChange}
                      isClearable={false}
                      isDisabled={!hasWritePermission}
                    />
                  </div>
                </SettingRow>

                <SettingRow
                  title="Stripe Summary Frequency"
                  description="Invoices and transactions pulled from Stripe will be aggregated in the Live Financials based on the selected frequency."
                  info={
                    <>
                      {stripeConnectedBank && stripeFrequencyLog && (
                        <span className="t-text-body-sm t-text-text-30 t-italic">
                          Last updated by: {stripeFrequencyLog.changed_by.name}{" "}
                          on {formatDate(stripeFrequencyLog.created_at)}
                        </span>
                      )}
                      {!stripeConnectedBank && (
                        <div className="t-flex t-gap-1 t-flex-row t-items-center t-text-body-sm t-text-text-30 sm:t-block">
                          <span className="t-inline-flex t-align-middle sm:t-mr-1">
                            <Info color="currentColor" size="13" stroke="1.2" />
                          </span>
                          <span>Connect your Stripe account to update.</span>{" "}
                          <ConditionalLink
                            to={`/books/data-sources${search}&type=${ACCOUNT_CONNECTION_TYPE.REVENUE}`}
                            className="t-text-purple hover:!t-underline"
                          >
                            Connect Stripe
                          </ConditionalLink>
                        </div>
                      )}
                    </>
                  }
                >
                  <div className="t-w-56 sm:t-w-full">
                    <Combobox
                      options={STRIPE_FREQUENCY_OPTIONS}
                      value={
                        stripeConnectedBank
                          ? STRIPE_FREQUENCY_OPTIONS.find(
                              (freq) =>
                                freq.value ===
                                lockDateData?.stripe_aggregate_frequency
                            )
                          : null
                      }
                      onChange={onStripeFrequencyChange}
                      isClearable={false}
                      isDisabled={!stripeConnectedBank || !hasWritePermission}
                    />
                  </div>
                </SettingRow>

                <SettingRow
                  title="Lock Financials"
                  description="We'll lock everything up to (and including) that day for everyone to keep your financials clean and final."
                  info={
                    lockDateData?.accounting_lock_date && (
                      <span className="t-text-body-sm t-text-text-30 t-italic">
                        Last updated by: {logsData?.logs?.[0]?.changed_by.name}{" "}
                        on {formatDate(logsData?.logs?.[0]?.created_at)}
                      </span>
                    )
                  }
                >
                  {lockDateData?.accounting_lock_date ? (
                    <div className="t-flex t-items-center t-gap-2">
                      <p className="t-text-body-sm t-p-1 t-m-0 t-border-solid t-border-neutral-0 t-border-0 t-rounded t-pr-1">
                        {formatDate(lockDateData?.accounting_lock_date)}
                      </p>
                      <ConditionalToolTip
                        propClass="!t-text-left"
                        condition={
                          !canUpdate && (
                            <div className="t-flex t-gap-2 t-flex-col">
                              <p className="t-m-0 t-text-subtitle">
                                Access Restricted
                              </p>
                              <p className="t-m-0 t-text-body-sm t-w-64">
                                You don't have access to edit the lock date.
                                Please reach out to us via{" "}
                                <ConditionalLink
                                  className="t-text-white !t-underline"
                                  to={`/chat/${group.secondary_channel_url}`}
                                >
                                  chat
                                </ConditionalLink>{" "}
                                for assistance.
                              </p>
                            </div>
                          )
                        }
                      >
                        <span>
                          <Button
                            size="xsmall"
                            onClick={handleEditClick}
                            customType="ghost_icon"
                            disabled={!canUpdate || !hasWritePermission}
                          >
                            <span
                              className={classNames({
                                "t-text-text-30": canUpdate,
                                "t-text-neutral-30": !canUpdate,
                              })}
                            >
                              <EditPencil
                                strokeWidth="1.2"
                                color="currentColor"
                              />
                            </span>
                          </Button>
                        </span>
                      </ConditionalToolTip>

                      <ConditionalToolTip
                        propClass="!t-text-left"
                        condition={
                          !canUpdate && (
                            <div className="t-flex t-gap-2 t-flex-col">
                              <p className="t-m-0 t-text-subtitle">
                                Access Restricted
                              </p>
                              <p className="t-m-0 t-text-body-sm t-w-64">
                                You don't have access to edit the lock date.
                                Please reach out to us via{" "}
                                <ConditionalLink
                                  className="t-text-white !t-underline"
                                  to={`/chat/${group.secondary_channel_url}`}
                                >
                                  chat
                                </ConditionalLink>{" "}
                                for assistance.
                              </p>
                            </div>
                          )
                        }
                      >
                        <span>
                          <Button
                            size="xsmall"
                            onClick={handleRemoveLock}
                            customType="ghost_icon"
                            disabled={!canUpdate || !hasWritePermission}
                          >
                            <span
                              className={classNames({
                                "t-text-text-30": canUpdate,
                                "t-text-neutral-30": !canUpdate,
                              })}
                            >
                              <TrashSimple />
                            </span>
                          </Button>
                        </span>
                      </ConditionalToolTip>
                    </div>
                  ) : (
                    <LockedUi
                      feature={WRITE_PERMISSION_KEYS.ACCOUNTING}
                      blockedUi={<LockedButton text="Add lock" />}
                    >
                      <Button
                        customType="secondary"
                        size="small"
                        onClick={handleAddLockDateClick}
                        leftIcon={<PlusIcon size="12" />}
                      >
                        Add lock
                      </Button>
                    </LockedUi>
                  )}
                </SettingRow>

                <SettingRow
                  title="Import data from Quickbooks"
                  description="Import entire data (invoices, bills, transactions, chart of accounts, journal entries, customers, vendors, etc) from Quickbooks to resume bookkeeping within Inkle books."
                  info={
                    <>
                      {qboFileUploadData?.last_import_date && (
                        <span className="t-text-body-sm t-text-text-30 t-italic">
                          Last imported on{" "}
                          {formatDate(qboFileUploadData?.last_import_date)}
                        </span>
                      )}
                    </>
                  }
                >
                  <Button
                    customType="secondary"
                    size="small"
                    onClick={qboButtonConfig.onClick}
                    disabled={
                      qboButtonConfig.disabled ||
                      (qboButtonConfig.text !== "View" && !hasWritePermission)
                    }
                  >
                    {qboButtonConfig.text}
                  </Button>
                </SettingRow>

                {(isPracticePro || isAdmin) && (
                  <FinallyImportSettingRow entityId={entityId} />
                )}

                <SettingRow
                  title="Send completed financials via email"
                  description="When enabled, you'll receive completed financials automatically once books are closed for each period."
                  info={
                    lockDateData?.send_completed_financials_email && (
                      <div className="t-flex t-items-center t-gap-1.5">
                        {lockDateData.completed_financials_email_recipients
                          .length > 0 && (
                          <span className="t-text-body-sm-lite t-text-text-30 t-italic">
                            {lockDateData.completed_financials_email_recipients.join(
                              ", "
                            )}
                          </span>
                        )}
                        <Button
                          customType="ghost_icon"
                          size="xsmall"
                          onClick={editEmailModal.open}
                          disabled={!hasWritePermission}
                        >
                          <EditPencil strokeWidth="1.2" color="currentColor" />
                        </Button>
                      </div>
                    )
                  }
                >
                  <Switch
                    checked={
                      lockDateData?.send_completed_financials_email ?? false
                    }
                    onCheckedChange={onSendFinancialsToggle}
                    disabled={!hasWritePermission}
                  />
                </SettingRow>
              </SettingsAccordion.Content>
            </SettingsAccordion.Item>

            {/* Automation Section */}
            <div>
              <AutomationSettings
                lockDateData={lockDateData}
                subscriptionModal={subscriptionModal}
                handleAccountsReceivableToggle={handleAccountsReceivableToggle}
                handleAccountsPayableToggle={handleAccountsPayableToggle}
              />
            </div>
          </SettingsAccordion.Root>
        </div>

        {/* Modals */}
        <EditLockDateModal
          isOpen={editModal.isOpen}
          onClose={editModal.close}
          lockDateData={lockDateData}
          formValues={formValues}
          onSubmit={handleConfirmSubmit}
          isUpdating={isUpdating}
        />

        <RemoveLockDateModal
          isOpen={removeModal.isOpen}
          onClose={removeModal.close}
          onConfirm={confirmDeleteLock}
          isUpdating={isUpdating}
        />

        <EnableAccountsReceivableModal
          isOpen={enableAccountsReceivableModal.isOpen}
          onClose={enableAccountsReceivableModal.close}
          onConfirm={
            accountsType === "receivable"
              ? confirmEnableAccountsReceivable
              : confirmEnableAccountsPayable
          }
          isUpdating={isUpdating}
          type={accountsType}
        />

        <DisableAccountsReceivableModal
          isOpen={disableAccountsReceivableModal.isOpen}
          onClose={disableAccountsReceivableModal.close}
          onConfirm={
            accountsType === "receivable"
              ? confirmDisableAccountsReceivable
              : confirmDisableAccountsPayable
          }
          isUpdating={isUpdating}
          type={accountsType}
        />

        <EditFinancialsEmailModal
          isOpen={editEmailModal.isOpen}
          onClose={editEmailModal.close}
          emails={lockDateData?.completed_financials_email_recipients ?? []}
        />

        {importQBO.isOpen && <ImportQBO {...importQBO} entityId={entityId} />}
        <QBOImportReview
          isOpen={qboReviewModal.isOpen}
          onClose={qboReviewModal.close}
          onNext={qboReviewModal.close}
          entityId={entityId}
        />

        <Modal.Root
          open={taxYearConfirmModal.isOpen}
          onOpenChange={taxYearConfirmModal.close}
        >
          <Modal.Content size="small">
            <StatusModal type="warning" title="Are you sure?">
              <p className="t-m-0 t-text-body-sm t-text-text-30">
                This will also update{" "}
                <span className="t-font-bold">{entityDetail.name}</span>
                's tax year end to{" "}
                <span className="t-font-bold">{pendingTaxYear?.label}</span> in
                Inkle Tax.
              </p>
            </StatusModal>
            <Modal.Footer className="t-flex t-items-center t-justify-end t-gap-3">
              <Button onClick={taxYearConfirmModal.close}>Cancel</Button>
              <Button
                customType="primary"
                onClick={handleConfirmTaxYearChange}
                isLoading={isUpdating}
                disabled={isUpdating}
              >
                Update
              </Button>
            </Modal.Footer>
          </Modal.Content>
        </Modal.Root>

        <BooksSoftwareSubscriptionsModal
          open={subscriptionModal.isOpen}
          onClose={subscriptionModal.close}
          entityId={entityId}
          onProductSubscribe={subscriptionModal.close}
        />
      </Async.Success>
    </Async.Root>
  );
};
